import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, CreditCard, Loader2, Lock, Minus, Plus, Send, User } from 'lucide-react';
import { Button } from './ui/button';
import {
  validaAteco,
  validaCap,
  validaCodiceFiscale,
  validaEmail,
  validaFatturazioneElettronica,
  validaObbligatorio,
  validaPartitaIva,
  validaTelefono,
  datiDaCodiceFiscale,
} from '../lib/validazione';
import { province, regioneDiProvincia } from '../data/province';
import { ricordaAcquistoInCorso, tracciaAcquisto } from '../googleAds';
import { leggiProvenienza } from '../provenienza';
import { corsiPubblicabili } from '../data/corsiSicurezza';

const ENDPOINT = process.env.REACT_APP_ISCRIZIONI_URL;
// Chiave pubblica di Cloudflare Turnstile (anti-bot). Se non c'è, il controllo resta spento.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/** Verifica anti-bot Cloudflare Turnstile: il più delle volte è invisibile per chi compila. */
function Turnstile({ onToken }) {
  const box = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let id;
    let chiuso = false;
    const disegna = () => {
      if (chiuso || !box.current || !window.turnstile || id !== undefined) return;
      id = window.turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        language: 'it',
        callback: onToken,
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };
    if (window.turnstile) disegna();
    else {
      let script = document.getElementById('cf-turnstile');
      if (!script) {
        script = document.createElement('script');
        script.id = 'cf-turnstile';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', disegna);
    }
    return () => {
      chiuso = true;
      if (id !== undefined && window.turnstile) window.turnstile.remove(id);
    };
  }, [onToken]);
  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={box} className="mt-2" />;
}

const PASSI = ['Partecipanti', 'Intestazione', 'Anagrafiche', 'Riepilogo'];

// Per l'iscrizione alla piattaforma bastano nome, cognome, codice fiscale ed email:
// il resto dell'anagrafica lo completa il partecipante al primo accesso.
const partecipanteVuoto = (corsoId = '') => ({ nome: '', cognome: '', codiceFiscale: '', email: '', corsoId });

// Catalogo per la scelta del corso di ciascun partecipante (ordini aziendali).
const CATALOGO = corsiPubblicabili();
const OPZIONI_CORSI = CATALOGO.map((c) => ({
  valore: c.id,
  testo: `${c.titolo}${/\bore\b/.test(c.titolo) ? '' : ` · ${c.ore} ore`} · ${c.prezzo} €`,
}));

/** Data di nascita (AAAA-MM-GG) ricavata dal codice fiscale, per l'archivio ordini. */
function dataNascitaDaCf(cf) {
  const d = datiDaCodiceFiscale(cf);
  if (!d) return '';
  return `${d.annoNascita}-${String(d.mese).padStart(2, '0')}-${String(d.giorno).padStart(2, '0')}`;
}

const fatturazioneVuota = {
  tipo: 'azienda',
  ragioneSociale: '', partitaIva: '', indirizzo: '', cap: '', citta: '', provincia: '',
  sdi: '', pec: '', referente: '', email: '', telefono: '', ateco: '',
  nome: '', cognome: '', codiceFiscale: '',
};

function Campo({ label, value, onChange, errore, tipo = 'text', placeholder, maiuscolo, larghezza = '' }) {
  return (
    <div className={larghezza}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <input
        type={tipo}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(maiuscolo ? e.target.value.toUpperCase() : e.target.value)}
        className={`w-full px-4 py-2.5 rounded-lg border-2 bg-white transition-colors outline-none ${
          errore ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-black'
        }`}
      />
      {errore && <p className="text-sm text-red-600 mt-1">{errore}</p>}
    </div>
  );
}

function Selezione({ label, value, onChange, errore, opzioni, vuoto = '— seleziona —', larghezza = '' }) {
  return (
    <div className={larghezza}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-4 py-2.5 rounded-lg border-2 bg-white transition-colors outline-none ${
          errore ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-black'
        }`}
      >
        <option value="">{vuoto}</option>
        {opzioni.map((o) => (
          <option key={o.valore} value={o.valore}>{o.testo}</option>
        ))}
      </select>
      {errore && <p className="text-sm text-red-600 mt-1">{errore}</p>}
    </div>
  );
}

const OPZIONI_PROVINCE = province.map((p) => ({ valore: p.sigla, testo: `${p.nome} (${p.sigla})` }));


export default function IscrizioneCorso({ corso, onIndietro }) {
  const [passo, setPasso] = useState(0);
  const [partecipanti, setPartecipanti] = useState([partecipanteVuoto(corso.id)]);
  const [fatturazione, setFatturazione] = useState(fatturazioneVuota);
  const [consensi, setConsensi] = useState({ privacy: false, condizioni: false, attivazione: false, ccnl: false, marketing: false });
  const [errori, setErrori] = useState({});
  const [invio, setInvio] = useState(false);
  const [tokenAntibot, setTokenAntibot] = useState('');
  const [erroreInvio, setErroreInvio] = useState(null);
  const [metodoPagamento, setMetodoPagamento] = useState('carta');
  const [esito, setEsito] = useState(null);

  // Un'azienda può scegliere un corso diverso per ogni partecipante; un privato acquista il corso della pagina.
  const multiCorso = fatturazione.tipo === 'azienda';
  const corsoDi = (p) => (multiCorso && CATALOGO.find((c) => c.id === p.corsoId)) || corso;
  const totale = partecipanti.reduce((somma, p) => somma + corsoDi(p).prezzo, 0);
  const richiedeCcnl = partecipanti.some((p) => corsoDi(p).richiedeCcnl);

  const aggiornaPartecipante = (indice, campo, valore) => {
    setPartecipanti((prec) => prec.map((p, i) => (i === indice ? { ...p, [campo]: valore } : p)));
    setErrori((prec) => ({ ...prec, [`p${indice}_${campo}`]: undefined }));
  };

  const aggiornaFatturazione = (campo, valore) => {
    setFatturazione((prec) => ({ ...prec, [campo]: valore }));
    setErrori((prec) => ({ ...prec, [`f_${campo}`]: undefined }));
  };

  const cambiaNumero = (delta) => {
    setPartecipanti((prec) => {
      const nuovo = Math.min(50, Math.max(1, prec.length + delta));
      if (nuovo > prec.length) return [...prec, ...Array.from({ length: nuovo - prec.length }, () => partecipanteVuoto(corso.id))];
      return prec.slice(0, nuovo);
    });
  };

  const validaIntestazione = () => {
    const e = {};
    if (fatturazione.tipo === 'azienda') {
      e.f_ragioneSociale = validaObbligatorio(fatturazione.ragioneSociale, 'La ragione sociale');
      e.f_partitaIva = validaPartitaIva(fatturazione.partitaIva);
      e.f_referente = validaObbligatorio(fatturazione.referente, 'Il referente');
      const fe = validaFatturazioneElettronica(fatturazione.sdi, fatturazione.pec);
      if (fe) e.f_sdi = fe;
    } else {
      e.f_nome = validaObbligatorio(fatturazione.nome, 'Il nome');
      e.f_cognome = validaObbligatorio(fatturazione.cognome, 'Il cognome');
      e.f_codiceFiscale = validaCodiceFiscale(fatturazione.codiceFiscale);
    }
    e.f_indirizzo = validaObbligatorio(fatturazione.indirizzo, "L'indirizzo");
    e.f_cap = validaCap(fatturazione.cap);
    e.f_citta = validaObbligatorio(fatturazione.citta, 'La città');
    e.f_provincia = validaObbligatorio(fatturazione.provincia, 'La provincia');
    e.f_email = validaEmail(fatturazione.email);
    e.f_telefono = validaTelefono(fatturazione.telefono);
    e.f_ateco = validaAteco(fatturazione.ateco);
    return e;
  };

  const validaAnagrafiche = () => {
    const e = {};
    const visti = new Set();
    partecipanti.forEach((p, i) => {
      e[`p${i}_nome`] = validaObbligatorio(p.nome, 'Il nome');
      e[`p${i}_cognome`] = validaObbligatorio(p.cognome, 'Il cognome');
      e[`p${i}_codiceFiscale`] = validaCodiceFiscale(p.codiceFiscale);
      e[`p${i}_email`] = validaEmail(p.email);

      const cf = (p.codiceFiscale || '').toUpperCase();
      // la stessa persona può fare più corsi, ma non lo stesso corso due volte
      const chiave = `${cf}|${corsoDi(p).id}`;
      if (!e[`p${i}_codiceFiscale`] && visti.has(chiave)) {
        e[`p${i}_codiceFiscale`] = 'Questa persona è già iscritta allo stesso corso: scegli un corso diverso o togli la riga';
      }
      visti.add(chiave);
    });
    return e;
  };

  const puliti = (oggetto) => Object.fromEntries(Object.entries(oggetto).filter(([, v]) => v));

  const avanti = () => {
    let e = {};
    if (passo === 1) e = puliti(validaIntestazione());
    if (passo === 2) e = puliti(validaAnagrafiche());
    if (Object.keys(e).length) {
      setErrori(e);
      return;
    }
    setErrori({});
    setPasso((p) => Math.min(PASSI.length - 1, p + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const indietro = () => {
    if (passo === 0) return onIndietro();
    setPasso((p) => p - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const inviaEPaga = async () => {
    if (richiedeCcnl && !consensi.ccnl) {
      setErroreInvio('Per i corsi RLS devi confermare che il CCNL applicato ammette la formazione in e-learning.');
      return;
    }
    if (!consensi.privacy || !consensi.condizioni || !consensi.attivazione) {
      setErroreInvio('Per proseguire devi accettare le condizioni, l’informativa privacy e la richiesta di attivazione immediata.');
      return;
    }
    if (TURNSTILE_SITE_KEY && !tokenAntibot) {
      setErroreInvio('Attendi il completamento della verifica anti-bot qui sopra, poi riprova.');
      return;
    }
    if (!ENDPOINT) {
      setErroreInvio('Iscrizioni non ancora attive: riprova più tardi o scrivici.');
      return;
    }
    setInvio(true);
    setErroreInvio(null);
    try {
      const risposta = await fetch(`${ENDPOINT}/iscrizioni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corso: { id: corso.id, titolo: corso.titolo, ore: corso.ore, prezzo: corso.prezzo },
          fatturazione: {
            ...fatturazione,
            nazione: 'Italia',
            regione: regioneDiProvincia(fatturazione.provincia),
          },
          partecipanti: partecipanti.map((p) => ({
            ...p,
            corsoId: corsoDi(p).id,
            // sesso e data di nascita non li chiediamo: stanno nel codice fiscale
            sesso: (datiDaCodiceFiscale(p.codiceFiscale) || {}).sesso || '',
            dataNascita: dataNascitaDaCf(p.codiceFiscale),
          })),
          consensi,
          metodoPagamento,
          totale,
          turnstile: tokenAntibot || undefined,
          provenienza: leggiProvenienza() || undefined,
        }),
      });
      if (!risposta.ok) {
        const err = new Error('richiesta rifiutata');
        err.stato = risposta.status;
        throw err;
      }
      const dati = await risposta.json();
      if (metodoPagamento === 'carta') {
        if (!dati.checkoutUrl) throw new Error('risposta senza link di pagamento');
        ricordaAcquistoInCorso({ valore: totale, riferimento: dati.riferimento || '' });
        window.location.href = dati.checkoutUrl;
        return;
      }
      tracciaAcquisto({ valore: totale, riferimento: dati.riferimento || '' });
      setEsito({ riferimento: dati.riferimento || '', email: fatturazione.email });
    } catch (err) {
      // il token anti-bot vale una volta sola: se l'invio fallisce ne serve uno nuovo
      setTokenAntibot('');
      if (TURNSTILE_SITE_KEY && window.turnstile) window.turnstile.reset();
      setErroreInvio(
        err.stato === 429
          ? 'Troppi tentativi ravvicinati: attendi un minuto e riprova. I dati inseriti restano qui.'
          : err.stato === 403
          ? 'La verifica anti-bot non è andata a buon fine: attendi che si completi e riprova.'
          : metodoPagamento === 'carta'
          ? 'Non siamo riusciti ad avviare il pagamento. Riprova fra poco: i dati inseriti restano qui.'
          : "Non siamo riusciti a registrare l'iscrizione. Riprova fra poco: i dati inseriti restano qui."
      );
      setInvio(false);
    }
  };

  if (esito) {
    return (
      <div className="max-w-2xl mx-auto px-6 text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-6 text-gray-900" />
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Iscrizione registrata</h1>
        <p className="text-gray-600 leading-relaxed mb-6">
          Abbiamo inviato a <strong>{esito.email}</strong> gli estremi per il bonifico
          {esito.riferimento ? <> e il riferimento <strong>{esito.riferimento}</strong></> : null}.
          Indica il riferimento nella causale: serve ad abbinare il pagamento alla tua iscrizione.
        </p>
        <p className="text-gray-900 leading-relaxed mb-6 bg-gray-100 border-l-4 border-black px-4 py-3 text-left">
          <strong>Appena fatto il bonifico, invia la contabile</strong> a{' '}
          <a href="mailto:corsi@mariobruzzese.it" className="underline">corsi@mariobruzzese.it</a>
          {esito.riferimento ? <> indicando il riferimento <strong>{esito.riferimento}</strong></> : null}:
          così acceleriamo la pratica di iscrizione.
        </p>
        <p className="text-gray-600 leading-relaxed mb-10">
          Ricevuto il bonifico iscriviamo i partecipanti sulla piattaforma entro 24 ore. A ciascuno arriverà un’email
          con il link di accesso, nome utente e password: se non la trova, controlli la cartella spam.
        </p>
        <Button onClick={onIndietro} size="lg" className="bg-black text-white hover:bg-gray-800 px-8">
          Torna al catalogo
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6">
      <button onClick={indietro} className="inline-flex items-center gap-2 text-gray-600 hover:text-black mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        {passo === 0 ? 'Torna al catalogo' : 'Torna indietro'}
      </button>

      <div className="mb-10">
        <p className="text-sm font-medium text-gray-500 mb-2">Iscrizione al corso</p>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{corso.titolo}</h1>
        <p className="text-gray-600">
          {corso.ore} ore · e-learning asincrono · {corso.prezzo}&nbsp;€ a partecipante, IVA compresa
        </p>
        {corso.avviso && <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-4">{corso.avviso}</p>}
      </div>

      <div className="flex items-center gap-2 mb-10">
        {PASSI.map((nome, i) => (
          <div key={nome} className="flex-1">
            <div className={`h-1.5 rounded-full mb-2 transition-colors ${i <= passo ? 'bg-black' : 'bg-gray-200'}`} />
            <span className={`text-xs ${i === passo ? 'text-black font-semibold' : 'text-gray-400'}`}>{nome}</span>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={passo}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {passo === 0 && (
            <section className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  {multiCorso ? 'Quante iscrizioni vuoi fare?' : 'Quante persone devi iscrivere?'}
                </h2>
                <p className="text-gray-600 mb-5 text-sm">
                  {multiCorso
                    ? 'Un’iscrizione è una persona su un corso: per far fare due corsi allo stesso dipendente indica 2.'
                    : 'I dati di ciascun partecipante servono all’ente per intestare l’attestato.'}
                </p>
                <div className="flex items-center gap-5">
                  <button onClick={() => cambiaNumero(-1)} className="w-11 h-11 rounded-full border-2 border-gray-200 hover:border-black flex items-center justify-center transition-colors" aria-label="Togli un partecipante">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-3xl font-bold w-12 text-center">{partecipanti.length}</span>
                  <button onClick={() => cambiaNumero(1)} className="w-11 h-11 rounded-full border-2 border-gray-200 hover:border-black flex items-center justify-center transition-colors" aria-label="Aggiungi un partecipante">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">A chi intestiamo la fattura?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { id: 'azienda', titolo: 'Azienda o professionista', nota: 'Con partita IVA' },
                    { id: 'privato', titolo: 'Privato', nota: 'Con codice fiscale' },
                  ].map((opzione) => (
                    <button
                      key={opzione.id}
                      onClick={() => aggiornaFatturazione('tipo', opzione.id)}
                      className={`text-left p-5 rounded-xl border-2 transition-all ${
                        fatturazione.tipo === opzione.id ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="block font-semibold text-gray-900">{opzione.titolo}</span>
                      <span className="block text-sm text-gray-500 mt-0.5">{opzione.nota}</span>
                    </button>
                  ))}
                </div>
              </div>

              {multiCorso ? (
                <div className="bg-gray-50 rounded-xl p-6 text-gray-600">
                  <span className="block">
                    {partecipanti.length} iscrizion{partecipanti.length > 1 ? 'i' : 'e'}
                  </span>
                  <span className="block text-sm text-gray-500 mt-1">
                    Nel passo «Anagrafiche» scegli il corso di ogni iscrizione: puoi iscrivere persone diverse a
                    corsi diversi, o la stessa persona a più corsi. Lì vedi il costo di ciascuna iscrizione e il totale.
                  </span>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 flex items-center justify-between">
                  <span className="text-gray-600">
                    {partecipanti.length} × {corso.prezzo}&nbsp;€
                  </span>
                  <span className="text-right">
                    <span className="block text-2xl font-bold leading-none">{totale}&nbsp;€</span>
                    <span className="block text-xs text-gray-500 mt-1">IVA compresa</span>
                  </span>
                </div>
              )}
            </section>
          )}

          {passo === 1 && (
            <section className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900">Dati per la fattura</h2>
              {fatturazione.tipo === 'azienda' ? (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Campo label="Ragione sociale" value={fatturazione.ragioneSociale} onChange={(v) => aggiornaFatturazione('ragioneSociale', v)} errore={errori.f_ragioneSociale} larghezza="sm:col-span-2" />
                  <Campo label="Partita IVA" value={fatturazione.partitaIva} onChange={(v) => aggiornaFatturazione('partitaIva', v)} errore={errori.f_partitaIva} />
                  <Campo label="Referente" value={fatturazione.referente} onChange={(v) => aggiornaFatturazione('referente', v)} errore={errori.f_referente} />
                  <Campo label="Codice destinatario SDI" value={fatturazione.sdi} onChange={(v) => aggiornaFatturazione('sdi', v)} errore={errori.f_sdi} maiuscolo placeholder="7 caratteri" />
                  <Campo label="PEC (in alternativa allo SDI)" value={fatturazione.pec} onChange={(v) => aggiornaFatturazione('pec', v)} />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Campo label="Nome" value={fatturazione.nome} onChange={(v) => aggiornaFatturazione('nome', v)} errore={errori.f_nome} />
                  <Campo label="Cognome" value={fatturazione.cognome} onChange={(v) => aggiornaFatturazione('cognome', v)} errore={errori.f_cognome} />
                  <Campo label="Codice fiscale" value={fatturazione.codiceFiscale} onChange={(v) => aggiornaFatturazione('codiceFiscale', v)} errore={errori.f_codiceFiscale} maiuscolo larghezza="sm:col-span-2" />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <Campo label="Indirizzo" value={fatturazione.indirizzo} onChange={(v) => aggiornaFatturazione('indirizzo', v)} errore={errori.f_indirizzo} larghezza="sm:col-span-2" />
                <Campo label="CAP" value={fatturazione.cap} onChange={(v) => aggiornaFatturazione('cap', v)} errore={errori.f_cap} />
                <Campo label="Città" value={fatturazione.citta} onChange={(v) => aggiornaFatturazione('citta', v)} errore={errori.f_citta} />
                <Selezione label="Provincia" value={fatturazione.provincia} onChange={(v) => aggiornaFatturazione('provincia', v)} errore={errori.f_provincia} opzioni={OPZIONI_PROVINCE} />
                <Campo label="Email" tipo="email" value={fatturazione.email} onChange={(v) => aggiornaFatturazione('email', v)} errore={errori.f_email} />
                <Campo label="Telefono" tipo="tel" value={fatturazione.telefono} onChange={(v) => aggiornaFatturazione('telefono', v)} errore={errori.f_telefono} />
                <Campo
                  label={fatturazione.tipo === 'azienda' ? 'Codice ATECO dell’azienda' : 'Codice ATECO dell’attività svolta'}
                  value={fatturazione.ateco}
                  onChange={(v) => aggiornaFatturazione('ateco', v)}
                  errore={errori.f_ateco}
                  placeholder="es. 47.11"
                />
                <p className="sm:col-span-2 -mt-2 text-xs text-gray-500">
                  Il codice ATECO compare sull’attestato come settore di appartenenza e determina la classe di rischio.
                  {fatturazione.tipo === 'azienda'
                    ? ' Lo trovi nella visura camerale o nel cassetto fiscale.'
                    : ' Indica quello dell’attività in cui lavori o per cui segui il corso.'}
                </p>
              </div>
            </section>
          )}

          {passo === 2 && (
            <section className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Dati dei partecipanti</h2>
                <p className="text-gray-600 text-sm">
                  Per l’iscrizione alla piattaforma bastano nome, cognome, codice fiscale ed email di ciascun partecipante: controlla che siano esatti. A quell’indirizzo arriverà l’email della piattaforma O.P.N. Italia Lavoro (non del nostro sito) con il link di accesso, nome utente e password, quindi dev’essere una casella che il partecipante legge davvero. Al primo accesso il partecipante completerà i suoi dati e inizierà il corso.
                </p>
              </div>

              {partecipanti.map((p, i) => (
                <div key={i} className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold text-gray-900">Partecipante {i + 1}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Campo label="Nome" value={p.nome} onChange={(v) => aggiornaPartecipante(i, 'nome', v)} errore={errori[`p${i}_nome`]} />
                    <Campo label="Cognome" value={p.cognome} onChange={(v) => aggiornaPartecipante(i, 'cognome', v)} errore={errori[`p${i}_cognome`]} />
                    <div className="sm:col-span-2">
                      <Campo label="Codice fiscale" value={p.codiceFiscale} onChange={(v) => aggiornaPartecipante(i, 'codiceFiscale', v)} errore={errori[`p${i}_codiceFiscale`]} maiuscolo />
                      {!errori[`p${i}_codiceFiscale`] && datiDaCodiceFiscale(p.codiceFiscale) && (
                        <p className="text-sm text-gray-500 mt-1">
                          Riconosciuto: nato{datiDaCodiceFiscale(p.codiceFiscale).sesso === 'F' ? 'a' : ''} il{' '}
                          {String(datiDaCodiceFiscale(p.codiceFiscale).giorno).padStart(2, '0')}/
                          {String(datiDaCodiceFiscale(p.codiceFiscale).mese).padStart(2, '0')}/
                          {datiDaCodiceFiscale(p.codiceFiscale).annoNascita}
                        </p>
                      )}
                    </div>
                    <Campo label="Email del partecipante" tipo="email" value={p.email} onChange={(v) => aggiornaPartecipante(i, 'email', v)} errore={errori[`p${i}_email`]} placeholder="Riceverà qui le credenziali di accesso" larghezza="sm:col-span-2" />
                    {multiCorso ? (
                      <Selezione label="Corso" value={corsoDi(p).id} onChange={(v) => aggiornaPartecipante(i, 'corsoId', v)} opzioni={OPZIONI_CORSI} larghezza="sm:col-span-2" />
                    ) : (
                      <p className="sm:col-span-2 text-sm text-gray-600">Corso: <strong>{corso.titolo}</strong> · {corso.ore} ore</p>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end text-sm text-gray-600">
                    Costo: <strong className="ml-1 text-gray-900">{corsoDi(p).prezzo}&nbsp;€</strong>
                  </div>
                </div>
              ))}

              <div className="bg-gray-50 rounded-xl p-6 flex items-center justify-between">
                <span className="text-gray-600">Totale · {partecipanti.length} partecipant{partecipanti.length > 1 ? 'i' : 'e'}</span>
                <span className="text-right">
                  <span className="block text-2xl font-bold leading-none">{totale}&nbsp;€</span>
                  <span className="block text-xs text-gray-500 mt-1">IVA compresa</span>
                </span>
              </div>
            </section>
          )}

          {passo === 3 && (
            <section className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Controlla e conferma</h2>

              <div className="border-2 border-gray-200 rounded-xl divide-y divide-gray-100">
                <div className="p-5 flex justify-between gap-4">
                  <span className="text-gray-600">Intestatario</span>
                  <span className="font-medium text-right">
                    {fatturazione.tipo === 'azienda' ? fatturazione.ragioneSociale : `${fatturazione.nome} ${fatturazione.cognome}`}
                  </span>
                </div>
                <div className="p-5 flex justify-between gap-4">
                  <span className="text-gray-600">Codice ATECO</span>
                  <span className="font-medium text-right">{fatturazione.ateco}</span>
                </div>
                <div className="p-5">
                  <span className="text-gray-600 block mb-2">Partecipanti e corsi</span>
                  <ul className="space-y-3">
                    {partecipanti.map((p, i) => (
                      <li key={i} className="flex justify-between gap-4">
                        <span>
                          <span className="font-medium">{p.nome} {p.cognome}</span>{' '}
                          <span className="text-gray-400">· {p.codiceFiscale}</span>
                          <span className="block text-sm text-gray-600">{corsoDi(p).titolo} · {corsoDi(p).ore} ore</span>
                        </span>
                        <span className="font-medium whitespace-nowrap">{corsoDi(p).prezzo}&nbsp;€</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-5 flex justify-between items-center bg-gray-50">
                  <span className="text-gray-600">Totale</span>
                  <span className="text-right">
                    <span className="block text-2xl font-bold leading-none">{totale}&nbsp;€</span>
                    <span className="block text-xs text-gray-500 mt-1">IVA compresa</span>
                  </span>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-3">Pagamento</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { id: 'carta', icona: CreditCard, titolo: 'Carta di credito o debito', nota: 'Paghi adesso in modo sicuro con Stripe, l’iscrizione parte subito' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMetodoPagamento(m.id)}
                      className={`text-left p-5 rounded-xl border-2 transition-all ${
                        metodoPagamento === m.id ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <m.icona className="w-5 h-5 mb-2" />
                      <span className="block font-semibold text-gray-900">{m.titolo}</span>
                      <span className="block text-sm text-gray-500 mt-0.5">{m.nota}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3 text-sm">
                {[
                  {
                    id: 'condizioni',
                    obbligatorio: true,
                    testo: (
                      <>
                        Ho letto e accetto le{' '}
                        <a href="/condizioni-vendita" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">
                          condizioni di vendita
                        </a>
                        .
                      </>
                    ),
                  },
                  {
                    id: 'privacy',
                    obbligatorio: true,
                    testo: (
                      <>
                        Ho letto l’
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">
                          informativa privacy
                        </a>{' '}
                        e acconsento al trattamento dei dati, anche dei partecipanti che ho inserito, per l’iscrizione
                        al corso.
                      </>
                    ),
                  },
                  {
                    id: 'attivazione',
                    obbligatorio: true,
                    testo: (
                      <>
                        Chiedo che il corso sia attivato subito, senza attendere i 14 giorni di ripensamento. Se
                        acquisto come consumatore, prendo atto che perdo il diritto di recesso nel momento in cui il
                        corso viene reso disponibile sulla piattaforma.
                      </>
                    ),
                  },
                  ...(richiedeCcnl
                    ? [{
                        id: 'ccnl',
                        obbligatorio: true,
                        testo: (
                          <>
                            Dichiaro che il CCNL applicato in azienda, o l’accordo collettivo di riferimento, consente
                            di svolgere la formazione RLS in modalità e-learning.
                          </>
                        ),
                      }]
                    : []),
                  {
                    id: 'marketing',
                    obbligatorio: false,
                    testo: <>Acconsento a ricevere comunicazioni su scadenze formative e nuovi corsi.</>,
                  },
                ].map((c) => (
                  <label key={c.id} className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consensi[c.id]}
                      onChange={(e) => setConsensi((prec) => ({ ...prec, [c.id]: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-black"
                    />
                    <span className="text-gray-700">
                      {c.testo} {c.obbligatorio && <span className="text-red-500">*</span>}
                    </span>
                  </label>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-600">
                <p className="mb-2">
                  Soggetto formatore: <strong>O.P.N. Italia Lavoro – CNL – EBAFoS</strong>; soggetto attuatore
                  MB Consulting, in convenzione con O.P.N. Italia Lavoro. Entro 24 ore lavorative dal pagamento ogni
                  partecipante riceve all’indirizzo indicato un’email dalla piattaforma O.P.N. Italia Lavoro (non dal nostro sito) con il link di accesso, nome
                  utente e password; al primo accesso completa i suoi dati e inizia il corso.
                </p>
                <p className="mb-2">
                  <strong>Attenzione:</strong> l’email della piattaforma O.P.N. Italia Lavoro può finire nello spam o nella posta indesiderata:
                  avvisa i partecipanti di controllare anche lì.
                </p>
                <p>
                  Al superamento del test finale si riceve l’attestato, verificabile tramite QR code sul sito opnitalialavoro.it.
                </p>
              </div>

              <Turnstile onToken={setTokenAntibot} />

              {erroreInvio && (
                <div className="border-2 border-red-200 bg-red-50 text-red-700 rounded-xl p-4 text-sm">{erroreInvio}</div>
              )}
            </section>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between gap-4 mt-10 pt-8 border-t border-gray-100">
        <button onClick={indietro} className="text-gray-600 hover:text-black transition-colors">Indietro</button>
        {passo < PASSI.length - 1 ? (
          <Button onClick={avanti} size="lg" className="bg-black text-white hover:bg-gray-800 px-8">
            Continua <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={inviaEPaga} disabled={invio} size="lg" className="bg-black text-white hover:bg-gray-800 px-8">
            {invio ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attendi…</>
            ) : metodoPagamento === 'carta' ? (
              <><Lock className="w-4 h-4 mr-2" /> Vai al pagamento</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Invia l’iscrizione</>
            )}
          </Button>
        )}
      </div>

      {Object.keys(errori).length > 0 && (
        <p className="text-sm text-red-600 mt-4 text-right">Controlla i campi evidenziati prima di continuare.</p>
      )}

      {metodoPagamento === 'carta' && (
        <p className="flex items-center justify-end gap-1.5 text-xs text-gray-400 mt-6">
          <Check className="w-3 h-3" /> Pagamento gestito da Stripe · i dati della carta non passano da questo sito
        </p>
      )}
    </div>
  );
}
